/**
 * useBusinessData Hook
 * Custom hook for fetching business-related data (branches, accounts)
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchBranches, fetchAccounts } from '@/src/lib/api/slip-api'
import { Branch, BankAccount } from '@/src/types/slip'

interface UseBusinessDataResult {
  branches: Branch[]
  accounts: BankAccount[]
  loading: boolean
  error: string | null
  refetchBranches: () => Promise<void>
  refetchAccounts: (branchId?: string) => Promise<void>
}

export function useBusinessData(): UseBusinessDataResult {
  const [branches, setBranches] = useState<Branch[]>([])
  const [accounts, setAccounts] = useState<BankAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchBranchesData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await fetchBranches()
      
      if (response.success) {
        setBranches(response.data)
      } else {
        setError('Failed to fetch branches')
      }
    } catch (err) {
      // Fallback to mock data when API not available
      console.warn('Branches API not available, using mock data')
      setBranches([
        { id: '1', business_id: '1', branch_code: 'BR001', branch_name: 'สาขาหลัก', is_active: true, created_at: new Date().toISOString() },
        { id: '2', business_id: '1', branch_code: 'BR002', branch_name: 'สาขาสุขุมวิท', is_active: true, created_at: new Date().toISOString() },
      ])
      setError(null) // Don't show error
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchAccountsData = useCallback(async (branchId?: string) => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await fetchAccounts(branchId)
      
      if (response.success) {
        setAccounts(response.data)
      } else {
        setError('Failed to fetch accounts')
      }
    } catch (err) {
      // Fallback to mock data when API not available
      console.warn('Accounts API not available, using mock data')
      setAccounts([
        { id: '1', branch_id: '1', bank_code: 'KBANK', account_number: '1234567890', account_name: 'บัญชีหลัก', is_active: true, created_at: new Date().toISOString() },
        { id: '2', branch_id: '1', bank_code: 'SCB', account_number: '0987654321', account_name: 'บัญชีรอง', is_active: true, created_at: new Date().toISOString() },
      ])
      setError(null) // Don't show error
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    // Fetch initial data
    const fetchData = async () => {
      await fetchBranchesData()
      await fetchAccountsData()
    }
    fetchData()
  }, [fetchBranchesData, fetchAccountsData])

  return {
    branches,
    accounts,
    loading,
    error,
    refetchBranches: fetchBranchesData,
    refetchAccounts: fetchAccountsData,
  }
}
